import { useState, useCallback, useEffect, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useSubscription } from './useSubscription';
import { trackEvent } from '../lib/analytics';

// ── Types ──────────────────────────────────────────────────────────────────────
export type NudgeType =
  | 'limit_hit'
  | 'near_limit'
  | 'feature_block'
  | 'high_usage'
  | 'tenure'
  | 'report_interest';

export interface NudgeConfig {
  type: NudgeType;
  title: string;
  message: string;
  cta: string;
  icon: string;
  resource?: string;
  feature?: string;
  urgency: 'low' | 'medium' | 'high';
}

interface TriggerState {
  limitHits: { resource: string; ts: number }[];
  featureBlocks: { feature: string; ts: number }[];
  lastNudgeDate: string;
  dismissedToday: boolean;
  sessionTransactions: number;
  reportViews: number;
}

// ── localStorage helpers ────────────────────────────────────────────────────
const STORAGE_KEY = 'panda_conv_triggers';
const today = () => new Date().toISOString().split('T')[0];

const loadState = (userId: string): TriggerState => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return emptyState();
    const all = JSON.parse(raw) as Record<string, TriggerState>;
    const s = all[userId] ?? emptyState();
    // Reset dismissed flag if day changed
    if (s.lastNudgeDate !== today()) {
      s.dismissedToday = false;
    }
    return s;
  } catch {
    return emptyState();
  }
};

const emptyState = (): TriggerState => ({
  limitHits: [],
  featureBlocks: [],
  lastNudgeDate: '',
  dismissedToday: false,
  sessionTransactions: 0,
  reportViews: 0,
});

const saveState = (userId: string, state: TriggerState) => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    const all = raw ? (JSON.parse(raw) as Record<string, TriggerState>) : {};
    all[userId] = state;
    localStorage.setItem(STORAGE_KEY, JSON.stringify(all));
  } catch { /* ignore */ }
};

// ── Nudge message factory ──────────────────────────────────────────────────
const buildNudge = (type: NudgeType, resource?: string, feature?: string): NudgeConfig => {
  const resourceLabels: Record<string, string> = {
    accounts: 'contas bancárias',
    cards: 'cartões',
    goals: 'metas',
    transactions: 'lançamentos',
  };
  const resourceLabel = resource ? (resourceLabels[resource] ?? resource) : '';

  const featureLabels: Record<string, string> = {
    canAccessReports: 'Relatórios & Insights',
    canExportCsv: 'exportação para CSV',
    canExportPdf: 'exportação para PDF',
    canAccessAdvancedInsights: 'Insights Avançados',
    canUseMultipleCurrencies: 'multi-moedas',
  };
  const featureLabel = feature ? (featureLabels[feature] ?? 'este recurso') : 'este recurso';

  switch (type) {
    case 'limit_hit':
      return {
        type, icon: 'ri-lock-2-line', urgency: 'high',
        title: `Limite de ${resourceLabel} atingido`,
        message: `Você atingiu o limite do plano gratuito. Faça upgrade para continuar usando sem restrições.`,
        cta: 'Ver planos →',
        resource,
      };
    case 'near_limit':
      return {
        type, icon: 'ri-error-warning-line', urgency: 'medium',
        title: `Você está quase no limite`,
        message: `Faltam poucos ${resourceLabel} para atingir o limite do plano gratuito. Evite interrupções — faça upgrade agora.`,
        cta: 'Fazer upgrade',
        resource,
      };
    case 'feature_block':
      return {
        type, icon: 'ri-vip-crown-line', urgency: 'medium',
        title: `${featureLabel} requer upgrade`,
        message: `Você tentou acessar ${featureLabel}. Este recurso está disponível nos planos pagos.`,
        cta: 'Desbloquear agora',
        feature,
      };
    case 'high_usage':
      return {
        type, icon: 'ri-fire-line', urgency: 'medium',
        title: 'Você está usando muito o PandaPRO',
        message: 'Você registrou muitos lançamentos hoje — sinal de que está levando as finanças a sério! O plano pago remove todos os limites.',
        cta: 'Ver planos Premium',
      };
    case 'tenure':
      return {
        type, icon: 'ri-time-line', urgency: 'low',
        title: 'Já faz uma semana com o PandaPRO',
        message: 'Você está usando o PandaPRO há 7+ dias. Que tal desbloquear relatórios, metas ilimitadas e muito mais?',
        cta: 'Ver o que você ganha',
      };
    case 'report_interest':
      return {
        type, icon: 'ri-bar-chart-2-line', urgency: 'medium',
        title: 'Você quer ver seus relatórios',
        message: 'Você tentou acessar os relatórios mais de uma vez. Desbloqueie análises completas com gráficos, comparativos e exportação.',
        cta: 'Desbloquear relatórios',
        feature: 'canAccessReports',
      };
    default:
      return {
        type: 'high_usage', icon: 'ri-vip-crown-line', urgency: 'low',
        title: 'Conheça o PandaPRO Premium',
        message: 'Limite ilimitado, relatórios avançados, exportação e muito mais por um valor acessível.',
        cta: 'Ver planos',
      };
  }
};

// ── Hook ───────────────────────────────────────────────────────────────────────
export const useConversionTriggers = () => {
  const { user, profile } = useAuth();
  const { isPaid, isTrial, loading: subLoading } = useSubscription();
  const [state, setState] = useState<TriggerState>(emptyState());
  const [activeNudge, setActiveNudge] = useState<NudgeConfig | null>(null);
  const evaluatedRef = useRef(false);

  // Load state
  useEffect(() => {
    if (!user) return;
    setState(loadState(user.id));
  }, [user]);

  // Save state whenever it changes
  const updateState = useCallback((updater: (prev: TriggerState) => TriggerState) => {
    if (!user) return;
    setState((prev) => {
      const next = updater(prev);
      saveState(user.id, next);
      return next;
    });
  }, [user]);

  // ── Public tracking functions ──────────────────────────────────────────────
  const trackLimitHit = useCallback((resource: string) => {
    if (!user || isPaid || isTrial) return;
    trackEvent('limit_hit', { resource, plan: 'free' });
    updateState((prev) => ({
      ...prev,
      limitHits: [...prev.limitHits.slice(-9), { resource, ts: Date.now() }],
    }));
    // Immediately show nudge
    const nudge = buildNudge('limit_hit', resource);
    setActiveNudge(nudge);
    trackEvent('paywall_impression', { trigger: 'limit_hit', resource });
  }, [user, isPaid, isTrial, updateState]);

  const trackFeatureBlock = useCallback((feature: string) => {
    if (!user || isPaid || isTrial) return;
    trackEvent('feature_block', { feature, plan: 'free' });

    setState((prev) => {
      const newBlocks = [...prev.featureBlocks.slice(-9), { feature, ts: Date.now() }];
      const next = { ...prev, featureBlocks: newBlocks };
      saveState(user.id, next);

      // Decide nudge after updating blocks
      const sameFeature = newBlocks.filter((b) => b.feature === feature);
      const shouldNudgeReports = feature === 'canAccessReports' && sameFeature.length >= 1;
      const shouldNudgeGeneric = sameFeature.length >= 2;

      if (shouldNudgeReports || shouldNudgeGeneric) {
        const nudgeType = shouldNudgeReports ? 'report_interest' : 'feature_block';
        const nudge = buildNudge(nudgeType as NudgeType, undefined, feature);
        // Delay to avoid setState-within-render warning
        setTimeout(() => {
          setActiveNudge(nudge);
          trackEvent('paywall_impression', { trigger: nudgeType, feature });
        }, 50);
      }

      return next;
    });
  }, [user, isPaid, isTrial]);

  const trackTransactionCreated = useCallback(() => {
    if (!user || isPaid || isTrial) return;

    setState((prev) => {
      const newCount = prev.sessionTransactions + 1;
      const next = { ...prev, sessionTransactions: newCount };
      saveState(user.id, next);

      // High-usage nudge after 10 transactions in session
      if (newCount === 10 && !prev.dismissedToday) {
        setTimeout(() => {
          setActiveNudge(buildNudge('high_usage'));
          trackEvent('paywall_impression', { trigger: 'high_usage', count: newCount });
        }, 50);
      }
      return next;
    });
  }, [user, isPaid, isTrial]);

  const trackReportView = useCallback(() => {
    if (!user || isPaid || isTrial) return;
    updateState((prev) => ({
      ...prev,
      reportViews: prev.reportViews + 1,
    }));
  }, [user, isPaid, isTrial, updateState]);

  // ── Periodic evaluation (once per page load) ──────────────────────────────
  useEffect(() => {
    if (subLoading || !user || isPaid || isTrial || evaluatedRef.current) return;
    evaluatedRef.current = true;

    const s = loadState(user.id);
    if (s.dismissedToday) return;

    // Don't show if already shown recently
    const daysSinceLastNudge = s.lastNudgeDate
      ? Math.floor((Date.now() - new Date(s.lastNudgeDate).getTime()) / (1000 * 60 * 60 * 24))
      : 999;
    if (daysSinceLastNudge < 1) return;

    // Tenure nudge: user joined 7+ days ago and hasn't upgraded
    if (profile?.created_at) {
      const daysActive = Math.floor(
        (Date.now() - new Date(profile.created_at).getTime()) / (1000 * 60 * 60 * 24)
      );
      if (daysActive >= 7 && s.limitHits.length === 0 && s.featureBlocks.length === 0) {
        // Only show tenure nudge if no more urgent ones exist
        setTimeout(() => {
          setActiveNudge(buildNudge('tenure'));
          trackEvent('paywall_impression', { trigger: 'tenure', days_active: daysActive });
        }, 8000); // delay 8s to not interrupt on page load
      }
    }
  }, [user, isPaid, isTrial, subLoading, profile?.created_at]);

  // ── Dismiss ──────────────────────────────────────────────────────────────
  const dismissNudge = useCallback(() => {
    if (activeNudge) {
      trackEvent('upgrade_dismiss', { trigger: activeNudge.type });
    }
    setActiveNudge(null);
    updateState((prev) => ({
      ...prev,
      dismissedToday: true,
      lastNudgeDate: today(),
    }));
  }, [activeNudge, updateState]);

  const handleUpgradeClick = useCallback(() => {
    if (activeNudge) {
      trackEvent('upgrade_click', {
        trigger: activeNudge.type,
        resource: activeNudge.resource,
        feature: activeNudge.feature,
      });
    }
  }, [activeNudge]);

  return {
    activeNudge: (!isPaid && !isTrial) ? activeNudge : null,
    dismissNudge,
    handleUpgradeClick,
    trackLimitHit,
    trackFeatureBlock,
    trackTransactionCreated,
    trackReportView,
  };
};
